import { ActionStatus, ActionType, EventType, FileTypeBase, useTrackComponent } from 'opensolar-common'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { http } from '../../services/http'
import { isBlobUrl } from '../utils'
import { fetchFileTags, fileTagTypeForResourcePrefix, resolveTagUrlsForSlot as resolveTagUrls } from './privateFileTagUtils'
import { UPLOAD_STATUS, UploadStatus } from './uploadStatus'

export type { UploadStatus } from './uploadStatus'
export { UPLOAD_STATUS } from './uploadStatus'

export type FileRecordStatus = 'existing' | 'pending' | 'uploaded'

export type FileRecord = {
  slot: string
  status: FileRecordStatus
  uploadStatus: UploadStatus
  // For pending files - the File object
  file?: File
  // For uploaded/existing files - the FileTypeBase record
  fileRecord?: FileTypeBase
  // For existing files - the form value (for reference)
  existingValue?: { id: number } | string
  // Which API to use for upload (stored on pending so upload uses correct endpoint before state flushes)
  resource_prefix?: ResourcePrefix
  // Display properties
  name: string
  size?: number
  url?: string
  id?: number
}

export type ResourcePrefix = 'private' | 'public' | 'global_public'

export type UploadFileContextType = {
  uploadBySlot: (slot: string) => Promise<FileRecord[]>
  uploadAll: () => Promise<FileRecord[]>
  setFileRecords: (slot: string, records: FileRecord[], resource_prefix: ResourcePrefix) => void
  updateFileTags: (slot: string, tags: string[]) => void
  updateFileTagTitles: (slot: string, titles: string[]) => void
  resetUpload: () => void
  uploadStatus: UploadStatus
  fileRecords: FileRecord[] // Unified array of all file records
  fileTagsBySlot: Record<string, string[]>
  totalFilesCount: number
  totalPendingFilesCount: number
}

interface UploadInfo {
  fileRecord: FileTypeBase | null
  statusText: string
  fileTitle: string
}

// Helper to create FileRecord from File
export const createPendingFileRecord = (file: File, slot: string, resource_prefix: ResourcePrefix): FileRecord => {
  // Generate object URL for local preview - this will be stable and reused
  const objectUrl = URL.createObjectURL(file)
  return {
    slot,
    status: 'pending',
    uploadStatus: UPLOAD_STATUS.INITIAL,
    file,
    name: file.name,
    size: file.size,
    url: objectUrl, // Store stable object URL for preview
    resource_prefix,
  }
}

// Helper to create FileRecord from FileTypeBase
export const createUploadedFileRecord = (fileRecord: FileTypeBase, slot: string): FileRecord => {
  return {
    slot,
    status: 'uploaded',
    uploadStatus: UPLOAD_STATUS.UPLOAD_COMPLETE,
    fileRecord,
    name: fileRecord.title || fileRecord.file_contents || 'File',
    size: fileRecord.filesize,
    url: fileRecord.file_contents,
    id: fileRecord.id,
  }
}

// Helper to create FileRecord from existing form value
export const createExistingFileRecord = (
  existingValue: { id: number } | string | FileTypeBase,
  slot: string
): FileRecord => {
  if (typeof existingValue === 'object' && existingValue !== null && 'file_contents' in existingValue) {
    const fileContents = existingValue.file_contents
    const isBlob = typeof fileContents === 'string' && isBlobUrl(fileContents)
    return {
      slot,
      status: isBlob ? 'pending' : 'existing',
      uploadStatus: UPLOAD_STATUS.INITIAL,
      fileRecord: existingValue,
      name: existingValue.title || existingValue.file_contents || 'File',
      url: isBlob ? fileContents : undefined,
    }
  } else if (typeof existingValue === 'string') {
    // Check if it's a blob URL
    if (isBlobUrl(existingValue)) {
      return {
        slot,
        existingValue,
        status: 'pending',
        uploadStatus: UPLOAD_STATUS.INITIAL,
        name: 'File',
        url: existingValue,
      }
    }
    // Store in const to avoid TypeScript control flow narrowing issues with type guards
    const urlString = existingValue as string
    // URL string (not a blob URL)
    const urlWithoutQuery = urlString.split('?')[0]
    const fileName = urlWithoutQuery.split('/').pop() || 'File'
    let decodedName = fileName
    try {
      decodedName = decodeURIComponent(fileName)
    } catch (e) {
      // If decoding fails, use original
    }
    return {
      slot,
      existingValue: urlString,
      status: 'existing',
      uploadStatus: UPLOAD_STATUS.INITIAL,
      name: decodedName,
      url: urlString,
    }
  } else {
    // {id: number} object
    return {
      slot,
      existingValue,
      status: 'existing',
      uploadStatus: UPLOAD_STATUS.INITIAL,
      name: `File #${existingValue.id}`,
      id: existingValue.id,
    }
  }
}

const UploadFileContext = createContext<UploadFileContextType>({
  uploadBySlot: (_: string) => Promise.resolve([]),
  uploadAll: () => Promise.resolve([]),
  setFileRecords: () => {},
  updateFileTags: () => {},
  updateFileTagTitles: () => {},
  resetUpload: () => {},
  uploadStatus: UPLOAD_STATUS.INITIAL,
  fileRecords: [],
  fileTagsBySlot: {},
  totalFilesCount: 0,
  totalPendingFilesCount: 0,
})

type UploadFileProviderProps = {
  children: React.ReactNode
  orgId: number
  projectUrl?: string
  projectId?: string
}

export const UploadFileProvider = ({ children, orgId, projectUrl, projectId }: UploadFileProviderProps) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UPLOAD_STATUS.INITIAL)
  const [fileRecordsState, setFileRecordsState] = useState<FileRecord[]>([])
  // Ref to track fileRecordsState for use in callbacks/closures to avoid stale closures
  const fileRecordsStateRef = useRef<FileRecord[]>([])
  const [fileTagsBySlot, setfileTagsBySlot] = useState<Record<string, string[]>>({})
  const [fileTagTitlesBySlot, setFileTagTitlesBySlot] = useState<Record<string, string[]>>({})
  const fileTagTitlesBySlotRef = useRef<Record<string, string[]>>({})
  const [resourcePrefixBySource, setResourcePrefixBySource] = useState<Record<string, ResourcePrefix>>({})
  // Track active upload promises to ensure subsequent calls wait for previous uploads
  const activeUploadPromisesRef = useRef<Promise<FileRecord[]>[]>([])
  const { trackEvent } = useTrackComponent({
    componentKey: 'file_upload_context',
    eventName: 'File Upload Context',
  })

  fileRecordsStateRef.current = fileRecordsState
  fileTagTitlesBySlotRef.current = fileTagTitlesBySlot

  const resolveTagUrlsForSlot = async (
    slot: string,
    tagUrls: string[],
    resourcePrefix: ResourcePrefix
  ): Promise<string[]> => {
    const titles = fileTagTitlesBySlotRef.current[slot] || []
    const tagType = fileTagTypeForResourcePrefix(resourcePrefix)
    return resolveTagUrls(tagUrls, titles, () => fetchFileTags(tagType))
  }

  // API only has private/public; global_public uses public endpoints
  const toApiPrefix = (rp: ResourcePrefix): 'private' | 'public' => (rp === 'private' ? 'private' : 'public')

  function getBaseUrl(resource_prefix: ResourcePrefix) {
    if (resource_prefix === 'private') {
      if (projectId) {
        return `orgs/${orgId}/projects/${projectId}/presigned_uploads/`
      } else {
        return `orgs/${orgId}/presigned_uploads/`
      }
    } else {
      return `orgs/${orgId}/presigned_uploads_public/`
    }
  }

  async function getPreSignedUrl(data: any, file: File, resource_prefix: ResourcePrefix) {
    const metadata = resource_prefix === 'global_public' ? { ...data, scope: 'global' as const } : data
    const payload = {
      original_filename: data.title,
      file_tags: data.file_tags,
      metadata,
      content_type: file.type,
    }
    const res = await http.os.post(payload, { url: getBaseUrl(resource_prefix) })
    return res.data
  }

  const getSingleFileData = (file: File, tags: string[]): any => {
    return {
      title: file.name,
      project: projectUrl,
      file_tags: tags,
    }
  }

  async function uploadFileUsingPreSignedUrl(preSignedUrl: string, file_contents: File) {
    const res = await fetch(preSignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file_contents.type },
      body: file_contents,
    })
    if (!res.ok) {
      throw new Error(`Upload failed: ${res.status} ${res.statusText}`)
    }
    return res
  }

  const uploadFileConfirm = async (
    presigned_id: string,
    resource_prefix: ResourcePrefix
  ): Promise<{ file_id: number }> => {
    const url = `${getBaseUrl(resource_prefix)}${presigned_id}/confirm/`
    const res = await http.os.post({}, { url })
    const fileIdKey =
      resource_prefix === 'private'
        ? 'private_file_id'
        : resource_prefix === 'global_public'
          ? 'global_public_file_id'
          : 'public_file_id'
    return { file_id: res.data[fileIdKey] }
  }

  const fetchFileRecord = async (fileId: number, resource_prefix: ResourcePrefix): Promise<FileTypeBase> => {
    if (resource_prefix === 'global_public') {
      const res = await http.os.get({ url: `global_public_files/${fileId}/` })
      return res.data
    }
    const apiPrefix = toApiPrefix(resource_prefix)
    const resource = apiPrefix === 'private' ? 'private_files' : 'public_files'
    const url = `orgs/${orgId}/${resource}/${fileId}/`
    const res = await http.os.get({ url })
    return res.data
  }

  const uploadFile = async (
    data: any,
    file_contents: File,
    resource_prefix: ResourcePrefix
  ): Promise<{ fileRecord: FileTypeBase | null; statusText: string; fileTitle: string }> => {
    const fileTitle = data.title
    try {
      const preSignedUrlData = await getPreSignedUrl(data, file_contents, resource_prefix)
      await uploadFileUsingPreSignedUrl(preSignedUrlData.presigned_url, file_contents)
      const confirmResponse = await uploadFileConfirm(preSignedUrlData.id, resource_prefix)
      const fileRecord = await fetchFileRecord(confirmResponse.file_id, resource_prefix)
      return {
        fileTitle,
        fileRecord,
        statusText: 'Uploaded',
      }
    } catch (e) {
      console.error(`Error uploading file(s): ${fileTitle} ${e}`)
      return {
        fileTitle,
        fileRecord: null,
        statusText: `${e}`,
      }
    }
  }

  const uploadSingleFile = async (file: File, tags: string[], resource_prefix: ResourcePrefix): Promise<UploadInfo> => {
    const fileData = getSingleFileData(file, tags)
    return await uploadFile(fileData, file, resource_prefix)
  }

  // Shared upload logic
  const performUpload = useCallback(
    async (
      filterFn: (record: FileRecord) => boolean,
      options: {
        sourceName: string
        isCompleteUpload: boolean
        onEmptyResult?: () => FileRecord[]
      }
    ): Promise<FileRecord[]> => {
      const { sourceName, isCompleteUpload, onEmptyResult } = options

      trackEvent?.(EventType.USER_INTERACTION, { type: ActionType.CLICK }, { source: sourceName })

      // Wait for all previous uploads to complete before starting this one
      if (activeUploadPromisesRef.current.length > 0) {
        await Promise.all(activeUploadPromisesRef.current)
      }

      // Collect pending files based on filter - use ref to avoid stale closures
      const pendingRecords = fileRecordsStateRef.current.filter((record) => {
        return record.status === 'pending' && filterFn(record)
      })

      if (pendingRecords.length === 0) {
        // Handle empty result case (e.g., return existing uploaded files)
        if (onEmptyResult) {
          return onEmptyResult()
        }
        return []
      }

      const batchKeys = pendingRecords.map((r) => ({ slot: r.slot, url: r.url, file: r.file }))
      const matchesUploadBatch = (r: FileRecord) =>
        r.file && batchKeys.some((k) => k.slot === r.slot && k.file === r.file && k.url === r.url)

      setUploadStatus(UPLOAD_STATUS.UPLOADING)
      setFileRecordsState((prev) =>
        prev.map((r) => (matchesUploadBatch(r) ? { ...r, uploadStatus: UPLOAD_STATUS.UPLOADING } : r))
      )

      const uploadPromise = (async () => {
        const uploadResponses = await Promise.all(
          pendingRecords.map(async (record) => {
            if (!record.file) {
              throw new Error('Pending record missing file')
            }
            const resourcePrefixForSlot = (record.resource_prefix ?? resourcePrefixBySource[record.slot]) || 'private'
            const tagsForSource = await resolveTagUrlsForSlot(
              record.slot,
              fileTagsBySlot[record.slot] || [],
              resourcePrefixForSlot
            )
            const result = await uploadSingleFile(record.file, tagsForSource, resourcePrefixForSlot)
            return {
              ...result,
              slot: record.slot,
            }
          })
        )

        const failedUploads = uploadResponses.filter((ui) => !ui.fileRecord)
        const successfulUploads = uploadResponses.filter((ui) => ui.fileRecord)
        const uploadedFiles: FileTypeBase[] = successfulUploads.map((ui) => ui.fileRecord!)

        const eventData = {
          projectId,
          projectUrl,
          count: uploadedFiles.length,
          source: 'multiple_files_dialog',
        }

        if (failedUploads.length > 0) {
          const errorMessage = `Error uploading file(s): ${failedUploads[0].statusText}`
          trackEvent?.(EventType.ERROR_EVENT, { type: ActionType.SUBMIT, status: ActionStatus.ERROR }, eventData)
          setUploadStatus(UPLOAD_STATUS.UPLOAD_FAILED)
          throw new Error(errorMessage)
        }

        trackEvent?.(EventType.SYSTEM_EVENT, { type: ActionType.SUBMIT, status: ActionStatus.SUCCESS }, eventData)

        const prevRecords = fileRecordsStateRef.current
        const uploadedFileRecords: FileRecord[] = []
        let uploadedIndex = 0

        const nextRecords = prevRecords.map((record) => {
          if (record.status === 'pending' && matchesUploadBatch(record) && uploadedIndex < uploadedFiles.length) {
            const uploadedFileType = uploadedFiles[uploadedIndex]
            uploadedIndex++
            if (isBlobUrl(record.url)) {
              URL.revokeObjectURL(record.url)
            }
            const uploadedRecord: FileRecord = {
              ...record,
              fileRecord: uploadedFileType,
              status: 'uploaded',
              uploadStatus: UPLOAD_STATUS.UPLOAD_COMPLETE,
              name: uploadedFileType.title || uploadedFileType.file_contents || 'File',
              size: uploadedFileType.filesize,
              url: uploadedFileType.file_contents,
              id: uploadedFileType.id,
            }
            uploadedFileRecords.push(uploadedRecord)
            return uploadedRecord
          }
          return record
        })

        fileRecordsStateRef.current = nextRecords
        setFileRecordsState(nextRecords)

        if (isCompleteUpload) {
          setUploadStatus(UPLOAD_STATUS.UPLOAD_COMPLETE)
        } else {
          const allUploadsComplete = activeUploadPromisesRef.current.length < 2
          if (allUploadsComplete) {
            setUploadStatus(UPLOAD_STATUS.INITIAL)
          }
        }

        return uploadedFileRecords
      })()
        .catch((e) => {
          setFileRecordsState((prev) =>
            prev.map((r) =>
              matchesUploadBatch(r) && r.status === 'pending' ? { ...r, uploadStatus: UPLOAD_STATUS.UPLOAD_FAILED } : r
            )
          )
          const errorMessage = `Error uploading file(s): ${e}`
          console.error(errorMessage)
          trackEvent?.(EventType.ERROR_EVENT, { type: ActionType.SUBMIT, status: ActionStatus.ERROR }, { projectId })
          setUploadStatus(UPLOAD_STATUS.UPLOAD_FAILED)
          throw e
        })
        .finally(() => {
          activeUploadPromisesRef.current = activeUploadPromisesRef.current.filter((p) => p !== uploadPromise)
        })

      activeUploadPromisesRef.current.push(uploadPromise)

      return uploadPromise
    },
    [fileTagsBySlot, resourcePrefixBySource, trackEvent, projectId, projectUrl]
  )

  // Upload files for a specific slot (partial upload)
  const uploadBySlot = useCallback(
    async (slot: string): Promise<FileRecord[]> => {
      return performUpload((record) => record.slot === slot, {
        sourceName: slot,
        isCompleteUpload: false,
      })
    },
    [performUpload]
  )

  // Upload all pending files (complete upload)
  const uploadAll = useCallback(async (): Promise<FileRecord[]> => {
    return performUpload(
      () => true, // Include all pending records
      {
        sourceName: 'default',
        isCompleteUpload: true,
        onEmptyResult: () => {
          // No files to upload, return existing uploaded files - use ref to avoid stale closures
          return fileRecordsStateRef.current.filter((record) => record.status === 'uploaded')
        },
      }
    )
  }, [performUpload])

  const setFileRecordsForSlot = useCallback((slot: string, records: FileRecord[], resource_prefix: ResourcePrefix) => {
    // Store the resource_prefix for this slot
    setResourcePrefixBySource((prev) => ({
      ...prev,
      [slot]: resource_prefix,
    }))

    // Compute the next state eagerly from the ref (kept in sync with latest committed state)
    // so the ref can be updated synchronously before setFileRecordsState triggers a re-render.
    // This prevents a race where autoUpload's setTimeout(0) fires before React commits.
    const prevRecords = fileRecordsStateRef.current

    // Clean up blob URLs for pending records that are being replaced
    prevRecords.forEach((r) => {
      if (r.slot !== slot) return
      const stillExists = records.some((newRecord) => {
        if (r.status === 'pending' && newRecord.status === 'pending') return r.file === newRecord.file
        return r.id === newRecord.id && r.id !== undefined
      })
      if (!stillExists && r.status === 'pending' && r.url?.startsWith('blob:')) {
        URL.revokeObjectURL(r.url)
      }
    })

    const nextRecords = [...prevRecords.filter((r) => r.slot !== slot), ...records]
    // Update ref immediately — performUpload reads this ref, so it must be current
    // before any scheduled upload callback (e.g. setTimeout(0)) fires.
    fileRecordsStateRef.current = nextRecords
    setFileRecordsState(nextRecords)
  }, [])

  const updateFileTags = useCallback((slot: string, tags: string[]) => {
    setfileTagsBySlot((prev) => ({
      ...prev,
      [slot]: tags,
    }))
  }, [])

  const updateFileTagTitles = useCallback((slot: string, titles: string[]) => {
    setFileTagTitlesBySlot((prev) => {
      const next = {
        ...prev,
        [slot]: titles,
      }
      fileTagTitlesBySlotRef.current = next
      return next
    })
  }, [])

  const resetUpload = useCallback(() => {
    fileRecordsStateRef.current.forEach((record) => {
      if (record.status === 'pending' && record.url?.startsWith('blob:')) {
        URL.revokeObjectURL(record.url)
      }
    })
    fileRecordsStateRef.current = []
    setFileRecordsState([])
    setUploadStatus(UPLOAD_STATUS.INITIAL)
  }, [])

  const totalFilesCount = fileRecordsState.length
  const totalPendingFilesCount = fileRecordsState.filter((r) => r.status === 'pending').length

  const contextValue = useMemo(
    () => ({
      uploadBySlot,
      uploadAll,
      setFileRecords: setFileRecordsForSlot,
      updateFileTags,
      updateFileTagTitles,
      resetUpload,
      uploadStatus,
      fileRecords: fileRecordsState,
      fileTagsBySlot,
      totalFilesCount,
      totalPendingFilesCount,
    }),
    [
      uploadBySlot,
      uploadAll,
      setFileRecordsForSlot,
      updateFileTags,
      updateFileTagTitles,
      resetUpload,
      uploadStatus,
      fileRecordsState,
      fileTagsBySlot,
      totalFilesCount,
      totalPendingFilesCount,
    ]
  )

  return <UploadFileContext.Provider value={contextValue}>{children}</UploadFileContext.Provider>
}

export const useFileUploadContext = () => {
  return useContext(UploadFileContext)
}
